import { z } from "zod";

import { canonicalJson, sha256Hex, type JsonValue } from "./canonical.js";
import { CalcDecimal } from "./decimal.js";
import { categoryGroupSchema } from "./reference-ruleset.js";

const decimal = z.string().regex(/^(0|[1-9]\d*)(\.\d+)?$/);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const nullableApprovalFingerprint = sha256.nullable();

const approvalBaseSchema = z.object({
  second_transcription_by: z.string().min(1).nullable(),
  approved_by: z.string().min(1).nullable(),
  approved_at: z.iso.datetime().nullable(),
  approved_payload_sha256: nullableApprovalFingerprint,
});

const z1Schema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    first_transcription_by: z.string().min(1),
  }),
  blocking_issues: z.array(z.unknown()),
  categories: z.array(
    z.object({
      source_code: z.string().min(1),
      destinations: z.array(
        z.object({
          code: z.string().min(1),
          g_min: decimal,
          g_max: decimal,
        }),
      ),
    }),
  ),
  coverage: z.object({
    destinations: z.number().int(),
    intervals_transcribed: z.number().int(),
    second_transcription_matches: z.number().int(),
    calculation_enabled: z.boolean(),
  }),
});

const z2CoverageSchema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    structural_inventory_by: z.string().min(1),
    numeric_transcription_complete: z.boolean(),
  }),
  blocking_issues: z.array(z.unknown()),
  category_columns: z.array(categoryGroupSchema),
  pages: z.array(
    z.object({
      phase_groups: z.array(
        z.object({ service_codes: z.array(z.string().min(1)) }),
      ),
    }),
  ),
  coverage: z.object({
    service_codes_expected: z.number().int(),
    service_codes_inventoried: z.number().int(),
    theoretical_matrix_cells: z.number().int(),
    first_transcription_cells: z.number().int(),
    first_transcription_numeric_values: z.number().int(),
    first_transcription_not_applicable_cells: z.number().int(),
    second_transcription_matches: z.number().int(),
    cells_remaining_for_second_transcription: z.number().int(),
    calculation_enabled: z.boolean(),
  }),
});

const z2MatrixSchema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    first_transcription_by: z.string().min(1),
  }),
  value_order: z.array(categoryGroupSchema),
  blocking_issues: z.array(z.unknown()),
  services: z.array(
    z.object({
      code: z.string().min(1),
      q: z.array(decimal.nullable()).length(8),
    }),
  ),
});

export interface Dm140ReleaseArtifacts {
  readonly z1: unknown;
  readonly z2Coverage: unknown;
  readonly z2Matrix: unknown;
}

export interface ReleaseGateIssue {
  readonly artifact: "bundle" | "z1" | "z2Coverage" | "z2Matrix";
  readonly code: string;
  readonly message: string;
}

export interface ReleaseGateResult {
  readonly ready: boolean;
  readonly issues: readonly ReleaseGateIssue[];
}

export class RulesetReleaseGateError extends Error {
  readonly issues: readonly ReleaseGateIssue[];

  constructor(issues: readonly ReleaseGateIssue[]) {
    super("D.M. 140/2012 ruleset is not approved for calculation");
    this.name = "RulesetReleaseGateError";
    this.issues = issues;
  }
}

const expectedGroups = [
  "EDILIZIA",
  "STRUTTURE",
  "IMPIANTI",
  "VIABILITA",
  "IDRAULICA",
  "TIC",
  "AGRICOLTURA_AMBIENTE",
  "TERRITORIO_URBANISTICA",
] as const;

function addIssue(
  issues: ReleaseGateIssue[],
  artifact: ReleaseGateIssue["artifact"],
  code: string,
  message: string,
): void {
  issues.push({ artifact, code, message });
}

function unique(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function jsonClone(value: unknown): JsonValue {
  return JSON.parse(JSON.stringify(value)) as JsonValue;
}

export async function fingerprintReleaseArtifact(value: unknown): Promise<string> {
  const payload = jsonClone(value);
  if (payload === null || Array.isArray(payload) || typeof payload !== "object") {
    throw new TypeError("Release artifact must be a JSON object");
  }
  const approval = payload.approval;
  if (approval === null || Array.isArray(approval) || typeof approval !== "object") {
    throw new TypeError("Release artifact approval must be a JSON object");
  }
  approval.approved_payload_sha256 = null;
  return sha256Hex(canonicalJson(payload));
}

async function checkApprovalFingerprint(
  artifact: ReleaseGateIssue["artifact"],
  raw: unknown,
  expected: string | null,
  issues: ReleaseGateIssue[],
): Promise<void> {
  if (expected === null) {
    addIssue(issues, artifact, "APPROVED_FINGERPRINT_MISSING", "Missing approved payload fingerprint");
    return;
  }
  const actual = await fingerprintReleaseArtifact(raw);
  if (actual !== expected) {
    addIssue(issues, artifact, "APPROVED_FINGERPRINT_MISMATCH", "Artifact changed after approval fingerprinting");
  }
}

export async function evaluateDm140ReleaseGate(
  artifacts: Dm140ReleaseArtifacts,
): Promise<ReleaseGateResult> {
  const issues: ReleaseGateIssue[] = [];
  const z1Result = z1Schema.safeParse(artifacts.z1);
  const coverageResult = z2CoverageSchema.safeParse(artifacts.z2Coverage);
  const matrixResult = z2MatrixSchema.safeParse(artifacts.z2Matrix);

  if (!z1Result.success) {
    addIssue(issues, "z1", "STRUCTURE_INVALID", z1Result.error.message);
  }
  if (!coverageResult.success) {
    addIssue(issues, "z2Coverage", "STRUCTURE_INVALID", coverageResult.error.message);
  }
  if (!matrixResult.success) {
    addIssue(issues, "z2Matrix", "STRUCTURE_INVALID", matrixResult.error.message);
  }
  if (!z1Result.success || !coverageResult.success || !matrixResult.success) {
    return { ready: false, issues };
  }

  const z1 = z1Result.data;
  const coverage = coverageResult.data;
  const matrix = matrixResult.data;

  const checkCommon = (
    artifact: "z1" | "z2Coverage" | "z2Matrix",
    value: {
      status: string;
      safe_for_calculation: boolean;
      blocking_issues: unknown[];
      approval: {
        second_transcription_by: string | null;
        approved_by: string | null;
        approved_at: string | null;
      };
    },
  ): void => {
    if (value.status !== "approved") addIssue(issues, artifact, "STATUS_NOT_APPROVED", "Status must be approved");
    if (!value.safe_for_calculation) addIssue(issues, artifact, "SAFE_FLAG_FALSE", "safe_for_calculation must be true");
    if (value.blocking_issues.length !== 0) addIssue(issues, artifact, "BLOCKING_ISSUES_OPEN", "Blocking issues must be empty");
    if (value.approval.second_transcription_by === null) addIssue(issues, artifact, "SECOND_TRANSCRIPTION_MISSING", "Second transcriber is required");
    if (value.approval.approved_by === null) addIssue(issues, artifact, "APPROVER_MISSING", "Approver is required");
    if (value.approval.approved_at === null) addIssue(issues, artifact, "APPROVAL_DATE_MISSING", "Approval timestamp is required");
  };

  checkCommon("z1", z1);
  checkCommon("z2Coverage", coverage);
  checkCommon("z2Matrix", matrix);

  if (z1.approval.second_transcription_by === z1.approval.first_transcription_by) {
    addIssue(issues, "z1", "TRANSCRIBERS_NOT_INDEPENDENT", "First and second Z-1 transcribers must differ");
  }
  if (matrix.approval.second_transcription_by === matrix.approval.first_transcription_by) {
    addIssue(issues, "z2Matrix", "TRANSCRIBERS_NOT_INDEPENDENT", "First and second Z-2 transcribers must differ");
  }
  if (!coverage.approval.numeric_transcription_complete) {
    addIssue(issues, "z2Coverage", "NUMERIC_TRANSCRIPTION_INCOMPLETE", "Numeric transcription must be complete");
  }

  const destinations = z1.categories.flatMap((category) => category.destinations);
  const destinationCodes = destinations.map((destination) => destination.code);
  if (destinations.length !== 35 || z1.coverage.destinations !== 35 || z1.coverage.intervals_transcribed !== 35) {
    addIssue(issues, "z1", "Z1_COVERAGE_INVALID", "Z-1 must contain 35 destinations and intervals");
  }
  if (!unique(destinationCodes)) addIssue(issues, "z1", "Z1_CODES_DUPLICATED", "Z-1 destination codes must be unique");
  destinations.forEach((destination) => {
    if (new CalcDecimal(destination.g_min).greaterThan(destination.g_max)) {
      addIssue(issues, "z1", "Z1_INTERVAL_INVALID", `Invalid G interval for ${destination.code}`);
    }
  });
  if (z1.coverage.second_transcription_matches !== 35) addIssue(issues, "z1", "Z1_SECOND_MATCHES_INCOMPLETE", "All 35 Z-1 intervals must match");
  if (!z1.coverage.calculation_enabled) addIssue(issues, "z1", "Z1_CALCULATION_DISABLED", "Z-1 calculation flag must be enabled");

  const inventoryCodes = coverage.pages.flatMap((page) =>
    page.phase_groups.flatMap((phase) => phase.service_codes),
  );
  const matrixCodes = matrix.services.map((service) => service.code);
  const numericCells = matrix.services.flatMap((service) => service.q).filter((value) => value !== null).length;
  const nullCells = matrix.services.length * 8 - numericCells;

  if (!unique(inventoryCodes) || !unique(matrixCodes)) addIssue(issues, "bundle", "Z2_CODES_DUPLICATED", "Z-2 service codes must be unique");
  if (inventoryCodes.length !== 102 || matrixCodes.length !== 102 || inventoryCodes.some((code, index) => code !== matrixCodes[index])) {
    addIssue(issues, "bundle", "Z2_CODE_INVENTORY_MISMATCH", "Coverage and matrix must contain the same 102 ordered service codes");
  }
  if (matrix.value_order.some((group, index) => group !== expectedGroups[index])) {
    addIssue(issues, "z2Matrix", "CATEGORY_ORDER_INVALID", "Z-2 category order is invalid");
  }
  if (coverage.category_columns.some((group, index) => group !== expectedGroups[index])) {
    addIssue(issues, "z2Coverage", "CATEGORY_ORDER_INVALID", "Z-2 coverage category order is invalid");
  }

  const c = coverage.coverage;
  if (c.service_codes_expected !== 102 || c.service_codes_inventoried !== 102 || c.theoretical_matrix_cells !== 816 || c.first_transcription_cells !== 816) {
    addIssue(issues, "z2Coverage", "Z2_COVERAGE_INVALID", "Z-2 coverage must declare 102 services and 816 cells");
  }
  if (matrix.services.length * 8 !== 816 || numericCells !== 522 || nullCells !== 294 || c.first_transcription_numeric_values !== numericCells || c.first_transcription_not_applicable_cells !== nullCells) {
    addIssue(issues, "bundle", "Z2_CELL_ACCOUNTING_INVALID", "Z-2 must contain 522 numeric and 294 non-applicable cells");
  }
  if (c.second_transcription_matches !== 816 || c.cells_remaining_for_second_transcription !== 0) {
    addIssue(issues, "z2Coverage", "Z2_SECOND_MATCHES_INCOMPLETE", "All 816 Z-2 cells must match the second transcription");
  }
  if (!c.calculation_enabled) addIssue(issues, "z2Coverage", "Z2_CALCULATION_DISABLED", "Z-2 calculation flag must be enabled");

  const hashes = [z1.source.sha256, coverage.source.sha256, matrix.source.sha256];
  if (new Set(hashes).size !== 1) addIssue(issues, "bundle", "SOURCE_HASH_MISMATCH", "All artifacts must reference the same official source hash");

  await Promise.all([
    checkApprovalFingerprint("z1", artifacts.z1, z1.approval.approved_payload_sha256, issues),
    checkApprovalFingerprint("z2Coverage", artifacts.z2Coverage, coverage.approval.approved_payload_sha256, issues),
    checkApprovalFingerprint("z2Matrix", artifacts.z2Matrix, matrix.approval.approved_payload_sha256, issues),
  ]);

  return { ready: issues.length === 0, issues };
}

export async function assertDm140ReleaseReady(
  artifacts: Dm140ReleaseArtifacts,
): Promise<void> {
  const result = await evaluateDm140ReleaseGate(artifacts);
  if (!result.ready) throw new RulesetReleaseGateError(result.issues);
}
